import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CalendarIcon, ClockIcon, UserIcon, ArrowLeft, TagIcon } from "lucide-react";
import { SocialShare } from "@/components/SocialShare";
import { PostPdfExportButton } from "@/components/PostPdfExportButton";
import { SEO } from "@/components/SEO";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author_id: string;
  status: string;
  published_at: string;
  featured_image_url: string;
  category: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  staff_members?: {
    first_name: string;
    last_name: string;
  } | null;
  profiles?: {
    staff_members: {
      first_name: string;
      last_name: string;
    };
  } | null;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  

  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!slug) return;

      console.log('Fetching blog post for slug:', slug);

      try {
        // First try to fetch as authenticated user (author/admin can see drafts)
        let { data: postData, error: postError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .contains('blog_types', ['public'])
          .maybeSingle();

        // If no data found (could be RLS restriction), try public query
        if (!postData && !postError) {
          const publicResult = await supabase
            .from('blog_posts')
            .select('*')
            .eq('slug', slug)
            .eq('status', 'published')
            .contains('blog_types', ['public'])
            .maybeSingle();
          
          postData = publicResult.data;
          postError = publicResult.error;
        }

        console.log('Blog post query result:', { postData, postError });

        if (postError) {
          console.error('Blog post error:', postError);
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (!postData) {
          console.log('No blog post found for slug:', slug);
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Fetch author information using the public function
        console.log('Fetching author for ID:', postData.author_id);
        const { data: authorData, error: authorError } = await supabase.rpc('get_blog_author', {
          author_user_id: postData.author_id
        });
        
        console.log('Author query result:', { authorData, authorError });

        // Attach author info to post
        const postWithAuthor = {
          ...postData,
          staff_members: authorData && authorData.length > 0 ? authorData[0] : null
        };

        setPost(postWithAuthor as any);

        // Fetch related posts in the same category (only public)
        const { data: relatedData, error: relatedError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .contains('blog_types', ['public'])
          .eq('category', postData.category)
          .neq('id', postData.id)
          .limit(3)
          .order('published_at', { ascending: false });

        if (!relatedError && relatedData) {
          // Get author info for related posts using the public function
          const relatedAuthorIds = [...new Set(relatedData.map(post => post.author_id))];
          
          const authorPromises = relatedAuthorIds.map(async (authorId) => {
            const { data: authorData } = await supabase.rpc('get_blog_author', {
              author_user_id: authorId
            });
            return {
              userId: authorId,
              staffMember: authorData && authorData.length > 0 ? authorData[0] : null
            };
          });

          const authorResults = await Promise.all(authorPromises);

          // Create author lookup map
          const authorMap: Record<string, any> = {};
          authorResults.forEach(result => {
            if (result.staffMember) {
              authorMap[result.userId] = result.staffMember;
            }
          });

          // Attach author info to related posts
          const relatedWithAuthors = relatedData.map(post => ({
            ...post,
            staff_members: authorMap[post.author_id] || null
          }));

          setRelatedPosts(relatedWithAuthors as any);
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const textContent = content.replace(/<[^>]*>/g, '');
    const wordCount = textContent.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const getAuthorName = (post: BlogPost) => {
    if (post.profiles?.staff_members) {
      return `${post.profiles.staff_members.first_name} ${post.profiles.staff_members.last_name}`;
    }
    if (post.staff_members) {
      return `${post.staff_members.first_name} ${post.staff_members.last_name}`;
    }
    return 'Unknown Author';
  };

  const renderContent = (content: string) => {
    // Decode HTML entities if content appears to be double-encoded
    let processedContent = content;
    
    // Check if content has escaped HTML tags and decode them
    if (processedContent.includes('&lt;') || processedContent.includes('&gt;')) {
      // HARDENING, not a patched exploit. Decode entities in an INERT document so the
      // untrusted body is never materialised into a node owned by the live page.
      //
      // MEASURED 2026-08-17, headless Chrome 151, feeding the previous live-document
      // version a body containing a literal closing-textarea tag followed by an img with
      // an onerror handler and a script element: 0 child elements on the textarea,
      // 0 handlers or scripts run, 0 images in the live document, and the whole payload
      // came back intact in .value. The widely repeated 'the closing tag breaks out of
      // RCDATA and the rest is adopted into the page' claim is FALSE for this idiom, and
      // the spec says why: assigning innerHTML is a fragment parse, no start tag has been
      // emitted by that tokenizer, so no end tag is an 'appropriate end tag token' and the
      // closing tag stays character data. Do not re-derive that from first principles and
      // scare yourself; it was tested.
      // What is changed is the invariant it rests on. Safety used to depend on that one
      // subtle tokenizer rule holding in every engine, for a node whose ownerDocument was
      // the live page, on the PRE-sanitisation side of the boundary. createHTMLDocument
      // returns a document with no browsing context (verified: defaultView is null), so
      // the dependency is gone whatever the tokenizer does.
      // The textarea is kept ON PURPOSE rather than reading textContent off a parsed
      // document. Measured on a charted post body, textContent returns "Fig 13 < 4 and
      // 5 > 2" where this returns the full markup: it strips every tag out of any post
      // that merely mentions an escaped angle bracket in prose, which is most of the
      // figure-bearing ones. Output here is byte-identical to the old code on every case
      // tried, attack strings included.
      // Does NOT fix: renderContent's output still crosses the component boundary to
      // PostPdfExportButton unsanitised and is only sanitised inside that component, and
      // the app still ships no CSP.
      const inertDoc = document.implementation.createHTMLDocument('');
      const textarea = inertDoc.createElement('textarea');
      textarea.innerHTML = processedContent;
      processedContent = textarea.value;
      // May need to decode twice if double-encoded
      if (processedContent.includes('&lt;') || processedContent.includes('&gt;')) {
        textarea.innerHTML = processedContent;
        processedContent = textarea.value;
      }
    }

    // Fix attributes (like iframe src or anchor href) that contain nested <a> tags wrapping the URL
    // The editor corrupted URLs by wrapping them in <a> tags inside attribute values
    // e.g. href="<a href="https://...">https://...</a>" → href="https://..."
    // e.g. src="<a href="https://...">https://...</a>" → src="https://..."
    processedContent = processedContent.replace(
      /((?:src|href)=")(<a[^>]*href="([^"]+)"[^>]*>[^<]*<\/a>)(")/gi,
      '$1$3$4'
    );
    
    // Convert markdown images to HTML images
    const htmlContent = processedContent.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" class="w-full h-auto rounded-lg shadow-lg my-6" loading="lazy" />'
    );
    
    return htmlContent;
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            Loading article...
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="py-8">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-8">
              The article you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild>
              <Link to="/blog">Back to Blog</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      {post && (
        <SEO
          title={post.title}
          description={(post.excerpt || post.content || "").replace(/<[^>]+>/g, "").slice(0, 155)}
          path={`/blog/${post.slug}`}
          type="article"
        />
      )}
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Back to Blog */}
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>

        {/* Article Header */}
        <div className="mb-8">
          <div className="mb-4">
            <Badge className="mb-4">{post.category}</Badge>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>
          
          <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
            {post.excerpt}
          </p>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-b border-border pb-6">
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              <span className="font-medium">{getAuthorName(post)}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              <span>{formatDate(post.published_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <ClockIcon className="h-4 w-4" />
              <span>{estimateReadTime(post.content)}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <PostPdfExportButton
                title={post.title}
                excerpt={post.excerpt}
                contentHtml={renderContent(post.content)}
                author={getAuthorName(post)}
                publishedAt={post.published_at}
                category={post.category}
                featuredImageUrl={post.featured_image_url}
              />
              <SocialShare title={post.title} />
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {post.featured_image_url && (
          <div className="mb-8">
            <img 
              src={post.featured_image_url} 
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
            />
          </div>
        )}

        {/* Article Content */}
        <div className="prose prose-lg max-w-none mb-12">
          <div 
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContent(post.content)) }}
            className="text-foreground [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mb-4 [&>h2]:mt-8 
                       [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mb-3 [&>h3]:mt-6
                       [&>h4]:text-lg [&>h4]:font-semibold [&>h4]:mb-2 [&>h4]:mt-4
                       [&>p]:mb-4 [&>p]:leading-relaxed
                       [&>ul]:mb-4 [&>ul]:space-y-1 [&>ul>li]:ml-4 [&>ul>li]:list-disc
                       [&>ol]:mb-4 [&>ol]:space-y-1 [&>ol>li]:ml-4 [&>ol>li]:list-decimal
                       [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic
                       [&>strong]:font-semibold
                       [&>img]:w-full [&>img]:h-auto [&>img]:rounded-lg [&>img]:shadow-lg [&>img]:my-6
                       [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:mx-auto [&_iframe]:block [&_iframe]:rounded-lg [&_iframe]:my-6"
          />
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mb-12 pb-8 border-b border-border">
            <div className="flex items-center gap-2 flex-wrap">
              <TagIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground mr-2">Tags:</span>
              {post.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Card key={relatedPost.id} className="overflow-hidden">
                  <div className="aspect-video relative">
                    {relatedPost.featured_image_url ? (
                      <img 
                        src={relatedPost.featured_image_url} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="bg-gradient-to-br from-muted to-muted/50 w-full h-full flex items-center justify-center">
                        <span className="text-2xl">📄</span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2">
                      <Badge className="text-xs">{relatedPost.category}</Badge>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-sm line-clamp-2 mb-2">
                      <Link 
                        to={`/blog/${relatedPost.slug}`}
                        className="hover:text-primary"
                      >
                        {relatedPost.title}
                      </Link>
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(relatedPost.published_at)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-primary/5 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Need Professional Investment Advice?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Our investment insights are just the beginning. Get personalised advice 
            tailored to your specific financial situation.
          </p>
          <Button size="lg" asChild>
            <Link to="/contact">Book Your Consultation</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;